import { Link } from '@tanstack/react-router';
import { MessageSquare } from 'lucide-react';

import { ConversationListItemContent } from '@apps/web/components/shared/messages/conversation_list_item_content';
import { MessagesPane } from '@apps/web/components/shared/messages/messages_pane';
import { Spinner } from '@apps/web/components/ui/spinner';
import { useOrganizationConversations } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type OrganizationConversationMetadata } from '@libraries/typescript/api_client';

const CONVERSATIONS_PAGE_SIZE = 1;

type CaregiverConversationTimelineTabProps = {
    readonly organizationId: string;
    readonly caregiverId: string | null;
    readonly organizationTimezone: string | null;
    readonly onNavigateToConversation?: () => void;
};

export function CaregiverConversationTimelineTab({
    organizationId,
    caregiverId,
    organizationTimezone,
    onNavigateToConversation,
}: CaregiverConversationTimelineTabProps) {
    const { conversations, error, isLoading } = useOrganizationConversations(
        caregiverId ? organizationId : null,
        { reason: 'caregiver-conversation-timeline-tab' },
        undefined,
        {
            caregiverId: caregiverId ?? undefined,
            includeHidden: true,
            pageSize: CONVERSATIONS_PAGE_SIZE,
        },
    );

    if (!caregiverId) {
        return (
            <p className="text-muted-foreground text-sm">
                Select a caregiver to view conversations.
            </p>
        );
    }

    if (isLoading) {
        return (
            <div className="text-muted-foreground flex h-full items-center justify-center gap-2 text-sm">
                <Spinner size="md" />
                Loading conversation…
            </div>
        );
    }

    if (error && conversations.length === 0) {
        return (
            <p className="text-sm text-red-600 dark:text-red-400">
                Unable to load conversation.
            </p>
        );
    }

    const conversation = conversations[0] ?? null;
    const timezone = organizationTimezone ?? 'UTC';

    if (!conversation) {
        return (
            <div className="text-muted-foreground flex h-full items-center justify-center p-6 text-center text-sm">
                <div>
                    <MessageSquare className="mx-auto mb-2 h-8 w-8 opacity-60" />
                    No conversation found for this caregiver.
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-full min-h-0 flex-col overflow-hidden">
            <MessagesPane
                autoScrollToLatest={true}
                collapseToRecentDay={true}
                conversationId={conversation.id}
                markAsRead={false}
                messageAnalyticsSurface="web"
                organizationId={organizationId}
                refreshOnOpen={true}
                showComposer={true}
                showRating={false}
                timezone={timezone}
                renderScrollHeader={(fetchedConversation) => (
                    <CaregiverConversationHeader
                        conversation={fetchedConversation ?? conversation}
                        organizationId={organizationId}
                        onNavigateToConversation={onNavigateToConversation}
                        timezone={timezone}
                    />
                )}
            />
        </div>
    );
}

type CaregiverConversationHeaderProps = {
    readonly conversation: OrganizationConversationMetadata;
    readonly organizationId: string;
    readonly timezone: string;
    readonly onNavigateToConversation?: () => void;
};

function CaregiverConversationHeader({
    conversation,
    organizationId,
    onNavigateToConversation,
    timezone,
}: CaregiverConversationHeaderProps) {
    return (
        <div className="bg-background sticky top-0 z-10 border-b px-4 py-3">
            <div className="flex items-start justify-between gap-3">
                <ConversationListItemContent
                    conversation={conversation}
                    timezone={timezone}
                    className="min-w-0 flex-1"
                />
                <Link
                    to="/conversations"
                    search={{
                        conversationId: conversation.id,
                        organizationId,
                    }}
                    onClick={onNavigateToConversation}
                    className="text-muted-foreground hover:text-foreground shrink-0 rounded-sm text-xs font-medium focus-visible:ring-2 focus-visible:outline-none"
                >
                    Open full thread
                </Link>
            </div>
        </div>
    );
}
